import { useState, useContext, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import PageScaffold from "../../components/PageScaffold";
import { SUBSCRIPTIONS_PAGE_PATH } from "../subscriptions/SubscriptionsPage";
import ContactsList from "../../components/contacts_page/ContactsList";
import ContactsContext from "../../contexts/data/ContactsContext";
import { CalendarOutlined, CheckOutlined, ContactsOutlined, DownloadOutlined, FileAddOutlined, HourglassOutlined, MessageOutlined, PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import AuthenticationContext from "../../contexts/auth/AuthenticationContext";
import { formatNumber } from '@leedpulse-monorepo/utils';
import { UserRole, ContextStatus } from '@leedpulse-monorepo/enums';
import { Alert, Button, message, Modal, Result, Select, Tooltip } from "antd";
import SubscriptionsContext from "../../contexts/data/SubscriptionsContext";
import ScenariosContext from "../../contexts/data/ScenariosContext";
import ConfirmWarningModal from "../../components/@shared/ConfirmWarningModal";
import ReassignSenderModal from "../../components/contacts_page/modal/ReassignSenderModal";
import { useNavigate } from "react-router-dom";

export const CONTACTS_PAGE_PATH = "/contacts";
export const CONTACTS_PAGE_TITLE = "Contacts";

interface ContactsPageProps {
  withIdScope?: boolean;
}

interface ContactsFilters {
  engaged: string;
  owner: string | null;
  scenario: string | null;
}

const ContactsPage: React.FC<ContactsPageProps> = ({ withIdScope }) => {
  const contactsContext = useContext(ContactsContext);
  const subscriptionsContext = useContext(SubscriptionsContext);
  const scenariosContext = useContext(ScenariosContext);
  const navigate = useNavigate();
  const authContext = useContext(AuthenticationContext);

  const { subscriptionId: subscriptionIdParam } = useParams<{ subscriptionId: string }>();
  const subscriptionId = withIdScope && subscriptionIdParam ? subscriptionIdParam : null;

  const [loadingExportFile, setLoadingExportFile] = useState(false);
  const [isReassignSenderOpen, setIsReassignSenderOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<ContactsFilters>(() => {
    const filterSaved = localStorage.getItem('contactsFilter');
    return filterSaved ? JSON.parse(filterSaved) : {
      engaged: '',
      owner: null,
      scenario: null,
    };
  });

  const initializedRef = useRef(false);

  const buildAlgoliaFilters = useCallback((): Record<string, unknown> => {
    const algoliaFilters: Record<string, unknown> = {};
    if (subscriptionsContext.selected?.apiKey) {
      algoliaFilters.apiKey = subscriptionsContext.selected.apiKey;
    }
    if (filters.engaged) algoliaFilters.engaged = filters.engaged;
    if (filters.owner) algoliaFilters.owner = filters.owner;
    if (filters.scenario) algoliaFilters.prospect_category = filters.scenario;
    return algoliaFilters;
  }, [subscriptionsContext.selected?.apiKey, filters]);

  const triggerSearch = useCallback((query: string, page = 0) => {
    contactsContext.search(query, page, contactsContext.hitsPerPage, buildAlgoliaFilters());
  }, [buildAlgoliaFilters, contactsContext.hitsPerPage]);

  // Initial load
  useEffect(() => {
    if (subscriptionId) {
      if (subscriptionsContext.selectionStatus !== ContextStatus.LOADING) {
        subscriptionsContext.fetchAndSelectById(subscriptionId);
      }
      scenariosContext.fetchAll();
      contactsContext.fetchOwnersById(subscriptionId);
    }
    return () => {
      subscriptionsContext.reset();
      contactsContext.reset();
    };
  }, []);

  // Trigger search when subscription is loaded (subscription-scoped mode)
  useEffect(() => {
    if (subscriptionId && subscriptionsContext.selected?.apiKey && !initializedRef.current) {
      initializedRef.current = true;
      triggerSearch(searchQuery);
    }
  }, [subscriptionsContext.selected?.apiKey]);

  // Trigger search on mount for global mode (no subscription)
  useEffect(() => {
    if (!subscriptionId && !initializedRef.current) {
      initializedRef.current = true;
      triggerSearch('');
    }
  }, []);

  // Re-search when filters change (subscription-scoped mode)
  useEffect(() => {
    if (!initializedRef.current) return;
    if (subscriptionId && subscriptionsContext.selected?.apiKey) {
      triggerSearch(searchQuery);
    }
    localStorage.setItem('contactsFilter', JSON.stringify(filters));
  }, [filters]);

  // Debounced search on query change
  useEffect(() => {
    if (!initializedRef.current) return;
    const timer = setTimeout(() => {
      triggerSearch(searchQuery);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    if (contactsContext.status === ContextStatus.ERROR && contactsContext.errorMessage) {
      message.error(contactsContext.errorMessage);
    }
  }, [contactsContext.status]);

  const handlePageChange = (page: number) => {
    triggerSearch(searchQuery, page);
  };

  const handleRefresh = () => {
    triggerSearch(searchQuery);
  };

  const handleSearch = (value: string) => {
    setSearchQuery(value);
  };

  const handleSearchCancel = () => {
    setSearchQuery('');
    triggerSearch('');
  };

  const showConfirmModal = () => {
    setIsModalOpen(true);
  };

  const handleConfirmDeletion = async () => {
    if (subscriptionsContext.selected) {
      await contactsContext.removeContactsNotAddressed(authContext.authentication, subscriptionsContext.selected.apiKey);
      if (contactsContext.status !== ContextStatus.ERROR) {
        setIsSuccessModalOpen(true);
      }
      setIsModalOpen(false);
    }
  };

  const updateFilters = (filterName: keyof ContactsFilters, value: string | null) => {
    setFilters((prev) => ({ ...prev, [filterName]: value }));
  };

  const isReadOnly =
    authContext.authentication == null ||
    ![UserRole.owner, UserRole.admin, UserRole.maintainer].includes(
      authContext.authentication?.role
    );

  const deleteContactSuccessModal = () => (
    <Modal
      centered
      open={isSuccessModalOpen}
      closeIcon={false}
      footer={null}
    >
      <Result
        status="success"
        title="Contacts supprimés avec succès"
        subTitle={
          <>
            <p><ContactsOutlined style={{ marginRight: 6 }} /> Nombre de contacts supprimés : {contactsContext.deleteResults.nbContacts}</p>
            <p><CalendarOutlined style={{ marginRight: 6 }} /> Nombre de jobs supprimés : {contactsContext.deleteResults.nbJobs}</p>
            <p><MessageOutlined style={{ marginRight: 6 }} /> Nombre de SMS supprimés : {contactsContext.deleteResults.nbSms}</p>
          </>
        }
        extra={[
          <Button key="ok" type="primary" onClick={() => {
            setIsSuccessModalOpen(false);
            handleRefresh();
          }}>
            Ok
          </Button>
        ]}
      />
    </Modal>
  );

  return (
    <PageScaffold
      title={`${subscriptionsContext.selected ? subscriptionsContext?.selected?.name : CONTACTS_PAGE_TITLE} (${formatNumber(contactsContext.contactsCount) || '0'})`}
      subtitle={"Gérez les contacts"}
      breadcrumbItems={
        withIdScope
          ? [
            {
              target: SUBSCRIPTIONS_PAGE_PATH,
              title: "Abonnements",
            },
            {
              target: SUBSCRIPTIONS_PAGE_PATH + "/" + subscriptionId,
              title: subscriptionsContext?.selected?.name || "Détail",
            },
            {
              title: "Contacts",
            },
          ]
          : [
            {
              title: "Contacts",
            },
          ]
      }
      controlsSettings={{
        searchLabel: "Email ou téléphone",
        onSearch: handleSearch,
        isLoading: contactsContext.status === ContextStatus.LOADING,
        onSearchCancel: handleSearchCancel,
        actionButtons:
          withIdScope && !isReadOnly
            ? [
              {
                label: "Nouveau contact",
                type: "primary",
                props: {
                  icon: <PlusOutlined />,
                  onClick: () => navigate("new"),
                  disabled: subscriptionsContext.selected?.sender.length === 0,
                },
              },
              {
                label: "Importer",
                type: "primary",
                props: {
                  icon: <FileAddOutlined />,
                  onClick: () => navigate("import"),
                  disabled: subscriptionsContext.selected?.sender.length === 0,
                },
              },
              {
                label: "Exporter",
                type: "primary",
                props: {
                  loading: loadingExportFile,
                  icon: <DownloadOutlined />,
                  onClick: async () => {
                    setLoadingExportFile(true);
                    const resExport = await contactsContext.exportContacts(authContext.authentication, subscriptionId);
                    if (resExport?.success && resExport?.message) {
                      message.success(resExport.message);
                    } else {
                      message.error(resExport.message || "Une erreur est survenue lors de l'export des contacts. Veuillez réessayer ultérieurement.");
                    }
                    setLoadingExportFile(false);
                  },
                },
              },
              {
                label: 'Rafraîchir',
                type: "primary",
                props: {
                  icon: <ReloadOutlined />,
                  onClick: handleRefresh,
                },
              },
              {
                label: '...',
                type: "primary",
                buttonType: 'dropdown',
                items: [{
                  key: '1',
                  label: (<a onClick={showConfirmModal}><span role="img" aria-label="bin">🗑️</span> Supprimer tous les contacts non engagés</a>),
                }, {
                  // `onClick` porté par l'item plutôt qu'un <a> : le menu antd rend
                  // déjà un élément cliquable accessible, et l'ancre sans href
                  // ajoutait quatre warnings jsx-a11y au cliquet.
                  key: '2',
                  label: '🔀 Réaffecter un numéro',
                  onClick: () => setIsReassignSenderOpen(true),
                }],
              },
            ]
            : [
              {
                label: 'Rafraîchir',
                type: "primary",
                props: {
                  icon: <ReloadOutlined />,
                  onClick: handleRefresh,
                },
              },
            ],
      }}
    >
      {subscriptionId && isReassignSenderOpen && (
        <ReassignSenderModal
          open={true}
          subscriptionId={subscriptionId}
          senders={subscriptionsContext.selected?.sender ?? []}
          auth={authContext.authentication}
          onSuccess={handleRefresh}
          onClose={() => setIsReassignSenderOpen(false)}
        />
      )}

      {withIdScope && subscriptionsContext.selected?.sender.length === 0 && (
        <Alert
          message="Veuillez configurer un numéro d'envoyeur pour cet abonnement avant de pouvoir importer des contacts."
          type="error"
          style={{ marginTop: '10px', marginBottom: '10px' }}
          banner
          closable
        />
      )}

      {subscriptionId && (
        <div style={{ marginBottom: '1rem' }}>
          <Select
            style={{ marginLeft: '0.4rem', width: '375px' }}
            defaultValue={filters.engaged}
            value={filters.engaged}
            onChange={(value) => updateFilters('engaged', value)}
            options={[
              { label: 'Tous les contacts', value: '' },
              {
                label: <span><CheckOutlined style={{ color: '#389e0d', marginRight: '4px' }} /> Contacts déjà engagés</span>,
                value: 'addressed',
              },
              {
                label: <span><HourglassOutlined style={{ color: '#ffd591', marginRight: '4px' }} /> Contacts à venir</span>,
                value: 'notaddressed',
              },
            ]}
          />
          <Tooltip title="Conseiller attitré des contacts">
            <Select
              style={{ marginLeft: '0.4rem', width: '375px' }}
              placeholder="Conseiller attitré des contacts..."
              value={filters.owner}
              allowClear
              onChange={(value) => updateFilters('owner', value)}
              options={contactsContext.selectionOwners.map((owner) => ({
                label: `${owner.firstname} ${owner.lastname}`,
                value: owner.uid,
              }))}
            />
          </Tooltip>
          <Tooltip title="Scénario des contacts">
            <Select
              style={{ marginLeft: '0.4rem', width: '375px' }}
              placeholder="Scénario des contacts..."
              value={filters.scenario}
              allowClear
              onChange={(value) => updateFilters('scenario', value)}
              options={scenariosContext.allScenarios
                .filter((scenario) => subscriptionsContext.selected?.idScenarios.includes(scenario.id))
                .map((scenario) => ({
                  label: scenario.name,
                  value: scenario.id,
                }))}
            />
          </Tooltip>
        </div>
      )}

      <ContactsList
        scopedId={subscriptionId}
        onPageChange={handlePageChange}
      />

      <ConfirmWarningModal
        text="Voulez-vous supprimer tous les contacts non engagés ?"
        isModalOpen={isModalOpen}
        isLoading={contactsContext.status === ContextStatus.LOADING}
        onOk={handleConfirmDeletion}
        onCancel={() => setIsModalOpen(false)}
      />
      {deleteContactSuccessModal()}
    </PageScaffold>
  );
};

export default ContactsPage;
